import type { OrderType } from "./core";
import { addDays } from "./core";
import type { ComplimentaryApproval, Expense, KitchenTicket, Order, Payment, PaymentMethod, PrintJob, Refund, Shift, ShiftSummary, StockMovement } from "./types";

const methods = (): Record<PaymentMethod, number> => ({ cash: 0, card: 0, bank_transfer: 0 });

export function summarizeShift(shift: Pick<Shift, "openingFloatMinor">, payments: readonly Payment[], refunds: readonly Refund[], expenses: readonly Expense[], supplierPayments: readonly { method: PaymentMethod; amountMinor: number }[] = []): { summary: ShiftSummary; expectedCashMinor: number } {
  const summary: ShiftSummary = { payments: methods(), refunds: methods(), cashExpensesMinor: 0, orderCount: new Set(payments.map((payment) => payment.orderId)).size };
  for (const payment of payments) summary.payments[payment.method] += payment.amountMinor;
  for (const refund of refunds) summary.refunds[refund.method] += refund.amountMinor;
  for (const expense of expenses) if (!expense.voided && expense.method === "cash") summary.cashExpensesMinor += expense.amountMinor;
  // Cash handed to suppliers from the drawer leaves it exactly as an expense does.
  for (const payment of supplierPayments) if (payment.method === "cash") summary.cashExpensesMinor += payment.amountMinor;
  return { summary, expectedCashMinor: shift.openingFloatMinor + summary.payments.cash - summary.refunds.cash - summary.cashExpensesMinor };
}

export type ReportInput = {
  orders: readonly Order[]; payments: readonly Payment[]; refunds: readonly Refund[]; expenses: readonly Expense[];
  complimentary: readonly ComplimentaryApproval[]; tickets: readonly KitchenTicket[]; printJobs: readonly PrintJob[];
};
type Tally = { label: string; count: number; valueMinor: number };
const tally = (map: Map<string, Tally>, label: string, valueMinor: number, count = 1) => {
  const entry = map.get(label) ?? { label, count: 0, valueMinor: 0 };
  entry.count += count; entry.valueMinor += valueMinor; map.set(label, entry);
};
const sorted = (map: Map<string, Tally>, by: "valueMinor" | "count" = "valueMinor") => [...map.values()].sort((a, b) => b[by] - a[by]);

/**
 * Builds every dashboard and export figure from the immutable records passed in. Sales are
 * attributed to the business date an order was created; cash movement to the date of each
 * payment or refund, so the two views reconcile through `outstandingMinor`.
 */
export function buildReport(input: ReportInput) {
  const valid = input.orders.filter((order) => order.status !== "voided");
  const voided = input.orders.filter((order) => order.status === "voided");
  const sum = (pick: (order: Order) => number, orders = valid) => orders.reduce((total, order) => total + pick(order), 0);
  const collected = methods(), refunded = methods();
  for (const payment of input.payments) collected[payment.method] += payment.amountMinor;
  for (const refund of input.refunds) refunded[refund.method] += refund.amountMinor;
  const collectedMinor = collected.cash + collected.card + collected.bank_transfer;
  const refundedMinor = refunded.cash + refunded.card + refunded.bank_transfer;

  const byType = new Map<string, Tally>(), byDay = new Map<string, Tally>(), items = new Map<string, Tally>(), staff = new Map<string, Tally>();
  const waiters = new Map<string, Tally>(), byHour = Array.from({ length: 24 }, (_, hour): Tally => ({ label: String(hour).padStart(2, "0"), count: 0, valueMinor: 0 }));
  const discountsByLabel = new Map<string, Tally>(), discountsByStaff = new Map<string, Tally>(), coupons = new Map<string, Tally>();
  const dishes = new Map<string, DishProfit>();
  for (const order of valid) {
    tally(byType, order.type satisfies OrderType, order.totals.subtotalMinor);
    tally(byDay, order.businessDate, order.totals.subtotalMinor);
    tally(staff, order.waiterName || order.createdByName, order.totals.subtotalMinor);
    if (order.waiterName) tally(waiters, order.waiterName, order.totals.subtotalMinor);
    // Sri Lanka has one fixed offset (UTC+5:30), so the local hour needs no time-zone database.
    const hour = byHour[new Date(Date.parse(order.createdAt) + 330 * 60_000).getUTCHours()]!;
    hour.count += 1; hour.valueMinor += order.totals.subtotalMinor;
    for (const line of order.lines) {
      if (line.voided) continue;
      const label = line.sizeName ? `${line.name} (${line.sizeName})` : line.name;
      tally(items, label, line.complimentary ? 0 : line.unitPriceMinor * line.quantity - line.discountMinor, line.quantity);
      // What each dish earns: its sales (after its own discounts) less the cost of what went into it. Free portions are left out.
      if (line.complimentary) continue;
      const dish = dishes.get(label) ?? { label, quantity: 0, salesMinor: 0, costMinor: 0, profitMinor: 0, marginBps: 0 };
      dish.quantity += line.quantity; dish.salesMinor += line.unitPriceMinor * line.quantity - line.discountMinor; dish.costMinor += line.unitCostMinor * line.quantity;
      dishes.set(label, dish);
    }
    for (const discount of order.discounts) {
      tally(discountsByLabel, discount.kind === "manual" ? "Manual discount" : discount.label, discount.amountMinor);
      tally(discountsByStaff, order.createdByName, discount.amountMinor);
      if (discount.kind === "coupon" && discount.code) tally(coupons, discount.code, discount.amountMinor);
    }
  }
  const compByKind = new Map<string, Tally>(), compByApprover = new Map<string, Tally>();
  for (const entry of input.complimentary) { tally(compByKind, entry.kind, entry.retailValueMinor); tally(compByApprover, entry.approvedByName, entry.retailValueMinor); }

  const prepared = input.tickets.filter((ticket) => ticket.readyAt && ticket.status !== "cancelled");
  const prepSeconds = prepared.map((ticket) => (Date.parse(ticket.readyAt!) - Date.parse(ticket.createdAt)) / 1000);
  const expensesByCategory = new Map<string, Tally>();
  for (const expense of input.expenses) if (!expense.voided) tally(expensesByCategory, expense.category, expense.amountMinor);

  const netSalesMinor = sum((order) => order.totals.subtotalMinor);
  const cogsMinor = sum((order) => order.totals.costMinor);
  const expensesMinor = [...expensesByCategory.values()].reduce((total, entry) => total + entry.valueMinor, 0);
  return {
    orderCount: valid.length,
    grossSalesMinor: sum((order) => order.totals.grossMinor - order.totals.complimentaryMinor),
    discountMinor: sum((order) => order.totals.discountMinor),
    netSalesMinor,
    serviceChargeMinor: sum((order) => order.totals.serviceChargeMinor),
    taxMinor: sum((order) => order.totals.taxMinor),
    billedMinor: sum((order) => order.totals.totalMinor),
    averageOrderMinor: valid.length > 0 ? Math.round(netSalesMinor / valid.length) : 0,
    collected, collectedMinor, refunded, refundedMinor,
    outstandingMinor: sum((order) => order.totals.totalMinor - order.paidMinor),
    states: {
      open: input.orders.filter((order) => order.status === "open").length,
      completed: input.orders.filter((order) => order.status === "completed").length,
      voided: voided.length,
      refunded: input.orders.filter((order) => order.paymentState === "refunded").length,
      partiallyRefunded: input.orders.filter((order) => order.paymentState === "partially_refunded").length,
      complimentary: valid.filter((order) => order.totals.complimentaryMinor > 0).length
    },
    voidedValueMinor: sum((order) => order.totals.totalMinor, voided),
    complimentary: {
      count: input.complimentary.length,
      retailValueMinor: input.complimentary.reduce((total, entry) => total + entry.retailValueMinor, 0),
      costMinor: input.complimentary.reduce((total, entry) => total + entry.costMinor, 0),
      byKind: sorted(compByKind), byApprover: sorted(compByApprover)
    },
    cogsMinor, expensesMinor, expensesByCategory: sorted(expensesByCategory),
    grossProfitMinor: netSalesMinor - refundedMinor - cogsMinor,
    netProfitMinor: netSalesMinor - refundedMinor - cogsMinor - expensesMinor,
    byType: sorted(byType), byDay: [...byDay.values()].sort((a, b) => a.label.localeCompare(b.label)),
    dishProfit: [...dishes.values()].map((dish) => ({ ...dish, profitMinor: dish.salesMinor - dish.costMinor, marginBps: dish.salesMinor > 0 ? Math.round((dish.salesMinor - dish.costMinor) * 10_000 / dish.salesMinor) : 0 }))
      .sort((a, b) => b.profitMinor - a.profitMinor),
    bestSellers: sorted(items, "count").slice(0, 25), itemSales: sorted(items, "count"), staff: sorted(staff), waiters: sorted(waiters), byHour,
    discountsByLabel: sorted(discountsByLabel), discountsByStaff: sorted(discountsByStaff), coupons: sorted(coupons),
    averagePrepSeconds: prepSeconds.length > 0 ? Math.round(prepSeconds.reduce((total, value) => total + value, 0) / prepSeconds.length) : 0,
    printFailures: input.printJobs.filter((job) => job.state === "failed" || job.state === "unknown").length
  };
}
export type Report = ReturnType<typeof buildReport>;
export type DishProfit = { label: string; quantity: number; salesMinor: number; costMinor: number; profitMinor: number; marginBps: number };

/** Monday of the week a business date falls in (Sri Lankan weeks run Monday to Sunday). */
export function weekStart(date: string): string {
  const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
  return addDays(date, -((weekday + 6) % 7));
}
/** Stock thrown away (waste movements), by item and by week, valued at the cost recorded with each movement. */
export function summarizeWastage(movements: readonly StockMovement[]) {
  const byItem = new Map<string, { label: string; quantityMilli: number; valueMinor: number; count: number }>(), byWeek = new Map<string, Tally>();
  let valueMinor = 0;
  for (const movement of movements) {
    if (movement.kind !== "waste") continue;
    const value = Math.round(Math.abs(movement.deltaMilli) * movement.unitCostMinor / 1000);
    valueMinor += value;
    const item = byItem.get(movement.itemName) ?? { label: movement.itemName, quantityMilli: 0, valueMinor: 0, count: 0 };
    item.quantityMilli += Math.abs(movement.deltaMilli); item.valueMinor += value; item.count += 1;
    byItem.set(movement.itemName, item);
    tally(byWeek, weekStart(movement.businessDate), value);
  }
  return { valueMinor, byItem: [...byItem.values()].sort((a, b) => b.valueMinor - a.valueMinor), byWeek: [...byWeek.values()].sort((a, b) => b.label.localeCompare(a.label)) };
}

export function toCsv(rows: readonly (readonly (string | number)[])[]): string {
  return rows.map((row) => row.map((cell) => {
    const text = String(cell);
    // Leading =,+,-,@ would be executed as a formula by spreadsheet software.
    const safe = typeof cell === "string" && /^[=+\-@]/.test(text) ? `'${text}` : text;
    return /[",\r\n]/.test(safe) ? `"${safe.replaceAll('"', '""')}"` : safe;
  }).join(",")).join("\r\n");
}
